/**
 * useInScopeColumnPicker — shared wiring for the VQE column pickers (SELECT
 * "add column", ORDER BY "add sort", GROUP BY "add group-by"). It folds the
 * resolver's flat column suggestions into table-grouped `CommandPickerPopover`
 * data that DEFAULTS to the tables actually in the query's FROM/JOIN, with a
 * "Show all tables" toggle and per-table "+ all" bulk action.
 *
 * Why a hook: all three pickers had byte-identical column-suggestion +
 * grouping + in-scope-filter + toggle logic. The only thing that differs is
 * what each does with a picked column (append a SELECT item / ORDER BY entry /
 * GROUP BY entry), so this hook owns everything up to selection and the
 * consumer wires `onSelect` / `onGroupAction` from `getColumn` /
 * `getColumnsForRelation`.
 *
 * `options.selection` names the clause the picker adds to. The picker then
 * check-marks columns already in it (`selectedColumnKeys`), `selectedIndexOf`
 * tells the consumer a pick is a remove, and the bulk action / its
 * `getUnselectedColumnsForRelation` only cover the columns still missing.
 *
 * The in-scope list is the statement's base tables (`querySources`) — plus,
 * in a drilled subquery, every enclosing level's tables it may correlate to
 * (`outerQuerySources`: innermost first, a shadowed name never). A CTE,
 * derived table or table function has no snapshot columns: the picker
 * reports it (`emptyMessage` / `notice`) instead of falling back to every
 * schema table, and a base table a CTE shadows is never offered.
 * `options.allowOutOfScope: false` (the filter builder) drops "Show all
 * tables" — a condition on a table the statement doesn't read cannot run.
 * `refs` carries the sources + engine every emitter qualifies a pick with.
 */

import { useCallback, useMemo, useState } from 'react';

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import type { Column } from '../../../../sql-resolver/types/schema';
import type { VqeEditContext } from '../../edit/editContext';
import { extractStructureForContext } from '../../edit/editContext';
import { resolvedRelationsForMatching } from '../../../../sql-resolver/service/snapshotRelations';
import { inQueryRelations } from '../../edit/edits/joinHelpers';
import { declaredCteKeys, resolveSourceRelation } from '../../../../sql-resolver/scope/querySources';
import type { ColumnRefs } from '../../edit/edits/selectItemHelpers';
import { useColumnSuggestions } from '../../edit/useSchemaSuggestions';
import type { CommandPickerGroup, CommandPickerScopeToggle } from './CommandPickerPopover';
import { columnsToGroups, pickerKeysFor, selectInScopeGroups } from './columnGroups';
import { toCommandPickerGroups } from './columnPickerGroups';
import { scopeSources } from './outerQuerySources';
import { selectedColumnKeys } from './selectedColumnKeys';
import { unresolvedSourcesCopy } from './unresolvedSourcesCopy';

/** The clause whose existing columns the picker check-marks. */
export type ColumnPickerSelection = 'select' | 'order-by' | 'group-by';

function clauseExpressions(
  structure: QueryStructure | null,
  selection: ColumnPickerSelection,
): readonly string[] {
  if (!structure) return [];
  if (selection === 'select') return (structure.selectItems ?? []).map((i) => i.expressionText);
  if (selection === 'order-by') return (structure.orderBy ?? []).map((i) => i.expressionText);
  return (structure.groupBy ?? []).map((i) => i.qualified.raw);
}

export interface InScopeColumnPicker {
  /** Grouped, table-headed picker data (in-scope by default, all when toggled). */
  readonly groups: CommandPickerGroup[];
  /** Footer toggle — present only when there are out-of-scope tables to reveal. */
  readonly scopeToggle: CommandPickerScopeToggle | undefined;
  readonly isLoading: boolean;
  readonly hasSnapshot: boolean;
  /** True when there is a snapshot AND at least one suggestable column. */
  readonly enabled: boolean;
  /** Resolve a picked item key back to its `Column`. */
  readonly getColumn: (key: string) => Column | undefined;
  /** Every column of a table, by its group key (`ColumnGroup.relationFolded`, for "+ all"). */
  readonly getColumnsForRelation: (relationFolded: string) => readonly Column[];
  /** Index of the clause item a picked key already appears at (→ remove), when `selection` is set. */
  readonly selectedIndexOf: (key: string) => number | undefined;
  /** The table's columns not yet in the clause (the "+ N remaining" action). */
  readonly getUnselectedColumnsForRelation: (relationFolded: string) => readonly Column[];
  /** The statement's sources + engine — what every emitter qualifies a picked column with. */
  readonly refs: ColumnRefs;
  /** Shown when nothing is listable because every source's columns are unknown (a CTE-only query). */
  readonly emptyMessage?: string;
  /** Shown under a non-empty list when some sources' columns are unknown. */
  readonly notice?: string;
}

export interface InScopeColumnPickerOptions {
  readonly selection?: ColumnPickerSelection;
  /** Offer "Show all tables" (default). The filter builder turns it off. */
  readonly allowOutOfScope?: boolean;
}

export function useInScopeColumnPicker(
  editContext: VqeEditContext,
  cacheKey: string,
  options: InScopeColumnPickerOptions = {},
): InScopeColumnPicker {
  const selection = options.selection;
  const allowOutOfScope = options.allowOutOfScope ?? true;
  const engine = editContext.service.engineId;
  const { track } = useAnalytics();
  const [showAllTables, setShowAllTables] = useState(false);
  const { columns, isLoading, hasSnapshot } = useColumnSuggestions({
    service: editContext.service,
    scope: editContext.scope,
    cacheKey,
  });

  // Tables actually in the query's FROM/JOIN. `scope.reachableTables` would
  // include every schema table (correct for IntelliSense, wrong here), so we
  // derive the in-query set from the live QueryStructure and default to it.
  // With `selection`, the marks must follow every committed edit (an added or
  // removed column), so the live buffer text joins the invalidation deps — a
  // string, so it only changes when the SQL does. Without it the deps stay as
  // before (no extra picker-identity churn for the filter builder).
  const liveSource = selection ? (editContext.getLiveSource?.() ?? null) : null;
  const structure = useMemo(() => {
    try {
      return extractStructureForContext(editContext);
    } catch {
      return null;
    }
    // `columns` is the invalidation proxy: it re-identifies when the scope's
    // reachable tables change (a FROM/JOIN edit) — exactly when the in-scope
    // set changes. service/documentId/statementId stay stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editContext.service, editContext.documentId, editContext.statementId, columns, liveSource]);
  const snapshot = editContext.service.getSchemaSnapshot();
  // The statement's own tables, + (UPDATE / DELETE) the ones its structure
  // doesn't carry, + (drilled subquery) the enclosing query's — each resolved
  // to the snapshot relation it names (schema included), so another schema's
  // same-named table is never taken for it. Keyed on the scope's frame kinds
  // rather than its identity: the wiring re-resolves the scope object on
  // every render.
  const frameKinds = scopeFrameKinds(editContext);
  const refs = useMemo<ColumnRefs>(
    () => {
      const { own, outer } = scopeSources(editContext, structure, snapshot);
      return { engine, sources: [...own, ...outer].map((s) => resolveSourceRelation(s, snapshot)) };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [structure, engine, snapshot, frameKinds, editContext.statementInfo?.id],
  );

  // Columns already in the clause → the index of the item that references them.
  // The statement's relations are resolved to the snapshot relations they name
  // (schema included) so a mark never lands on another schema's same-named table.
  const selectedIndex = useMemo(
    () =>
      selection
        ? selectedColumnKeys(
            clauseExpressions(structure, selection),
            resolvedRelationsForMatching(snapshot, inQueryRelations(structure, engine)),
            columns,
          )
        : null,
    [selection, structure, columns, engine, snapshot],
  );
  const selectedKeys = useMemo(
    () => (selectedIndex ? new Set(selectedIndex.keys()) : undefined),
    [selectedIndex],
  );

  const allGroups = useMemo(() => columnsToGroups(columns, refs), [columns, refs]);
  const { visible, all, hasOutOfScope, unresolved } = useMemo(
    () =>
      selectInScopeGroups(allGroups, {
        known: structure !== null,
        sources: refs.sources,
        shadowKeys: declaredCteKeys(structure, engine),
      }),
    [allGroups, refs, structure, engine],
  );
  const showingAll = allowOutOfScope && showAllTables;
  const activeGroups = showingAll ? all : visible;
  const { emptyMessage, notice } = useMemo(
    () => unresolvedSourcesCopy(unresolved, activeGroups.length > 0),
    [unresolved, activeGroups.length],
  );
  const groups = useMemo(
    () => toCommandPickerGroups(activeGroups, { withAddAll: true, selectedKeys }),
    [activeGroups, selectedKeys],
  );

  const keys = useMemo(() => pickerKeysFor(columns), [columns]);
  const lookup = useMemo(() => {
    const map = new Map<string, Column>();
    for (const c of columns) map.set(keys.columnKey(c), c);
    return map;
  }, [columns, keys]);

  const onToggleScope = useCallback(() => {
    const next = !showAllTables;
    setShowAllTables(next);
    track('feature:vqe_show_all_tables', { enabled: next });
  }, [showAllTables, track]);

  const scopeToggle = useMemo(
    () =>
      allowOutOfScope && (hasOutOfScope || showAllTables)
        ? { showingAll: showAllTables, onToggle: onToggleScope }
        : undefined,
    [allowOutOfScope, hasOutOfScope, showAllTables, onToggleScope],
  );

  // Memoize the whole return object so `picker` is REFERENTIALLY STABLE while
  // its real constituents (`groups`, `scopeToggle`, `lookup`, `columns`, …) are
  // unchanged. Returning a bare object literal here handed every consumer a new
  // `picker` identity on EVERY render, which churned `useVqeFilterColumns`'
  // `resolve`/`resolveByField` (`useCallback` keyed on `picker`) and, through
  // them, `VqeFilterChipBuilder`'s `resolution`/`enrichedParsed` memos — driving
  // the edit-mode re-sync effect into a `setDraft` re-render loop after any
  // external re-render. `getColumn`/`getColumnsForRelation` close over the
  // memoized `lookup`/`columns`, so they only re-identify when those change.
  return useMemo<InScopeColumnPicker>(
    () => ({
      groups,
      scopeToggle,
      isLoading,
      hasSnapshot,
      enabled: hasSnapshot && columns.length > 0,
      getColumn: (key) => lookup.get(key),
      getColumnsForRelation: (relationFolded) =>
        columns.filter((c) => keys.groupKey(c.relation) === relationFolded),
      selectedIndexOf: (key) => selectedIndex?.get(key),
      getUnselectedColumnsForRelation: (relationFolded) =>
        columns.filter(
          (c) => keys.groupKey(c.relation) === relationFolded && !selectedIndex?.has(keys.columnKey(c)),
        ),
      refs,
      ...(emptyMessage ? { emptyMessage } : {}),
      ...(notice ? { notice } : {}),
    }),
    [groups, scopeToggle, isLoading, hasSnapshot, columns, lookup, selectedIndex, keys, refs, emptyMessage, notice],
  );
}

/** `subquery>statement`-style signature of the context scope's frame chain. */
function scopeFrameKinds(editContext: VqeEditContext): string {
  const kinds: string[] = [];
  for (let s = editContext.scope ?? undefined; s; s = s.parent) kinds.push(s.scopeKind);
  return kinds.join('>');
}
